import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, ThumbsUp, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import heroFaqs from "@/assets/hero-faqs.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const FAQ_GROUPS_DATA: FaqGroup[] = [
  {
    category: "Shipping & Returns",
    items: [
      { q: "Where do you ship?", a: "We ship across the United States. International shipping is available on request — reach out and we'll get you a quote." },
      { q: "How long does shipping take?", a: "Domestic orders typically ship within 1–3 business days and arrive within 3–7 business days depending on your location." },
      { q: "What is your return policy?", a: "Unworn hats can be returned within 30 days of purchase for a full refund. Custom and embroidered orders are final sale." },
      { q: "Do you ship internationally?", a: "We don't offer international checkout yet, but if you're outside the US, contact us and we'll work something out." },
    ],
  },
  {
    category: "Products & Sizing",
    items: [
      { q: "What sizes do your hats come in?", a: "All of our hats are one size fits all snapbacks. Good for noggins all shapes and sizes." },
      { q: "Are your hats true to size?", a: "Yes. The adjustable snap closure dials in the fit comfortably for most adult head sizes." },
      { q: "What materials are used?", a: "We use durable cotton twill, structured foam fronts, and quality mesh backs built to hold up season after season." },
    ],
  },
  {
    category: "Custom Orders",
    items: [
      { q: "Do you offer custom embroidery?", a: "Yes — visit our Custom Hats page to start a custom order for your team, brand, or event." },
      { q: "What's the minimum order for custom hats?", a: "Our standard custom minimum is 24 hats. Reach out for larger runs and we'll get you taken care of." },
      { q: "How long do custom orders take?", a: "Most custom runs ship within 3–7 weeks from artwork approval. Rush options are available on request." },
    ],
  },
  {
    category: "Wholesale",
    items: [
      { q: "How do I become a dealer?", a: "Head to the Become a Dealer page to fill out our wholesale application. We'll be in touch within a few business days." },
    ],
  },
  {
    category: "Care",
    items: [
      { q: "How should I clean my hat?", a: "Spot clean with cold water and a soft brush. Avoid the washing machine to preserve structure and embroidery." },
    ],
  },
];

type FaqItem = { q: string; a: string };
type FaqGroup = { category: string; items: FaqItem[] };

export const Route = createFileRoute("/faqs")({
  head: () => ({
    meta: [
      { title: "FAQs | Coyote Creek Hat Co." },
      { name: "description", content: "Frequently asked questions about Coyote Creek hats, shipping, returns, and custom orders." },
      { property: "og:title", content: "FAQs | Coyote Creek Hat Co." },
      { property: "og:description", content: "Frequently asked questions about Coyote Creek Hat Co." },
      { property: "og:url", content: "https://coyotecreekhatco.com/faqs" },
    ],
    links: [{ rel: "canonical", href: "https://coyotecreekhatco.com/faqs" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_GROUPS_DATA.flatMap((g) =>
            g.items.map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          ),
        }),
      },
    ],
  }),
  component: FaqsPage,
});

const FAQ_GROUPS = FAQ_GROUPS_DATA;

const ALL_CATEGORIES = ["All", ...FAQ_GROUPS.map((g) => g.category)];

function FaqsPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    return FAQ_GROUPS
      .filter((g) => activeCategory === "All" || g.category === activeCategory)
      .map((g) => ({
        ...g,
        items: g.items.filter(
          (item) =>
            !query ||
            item.q.toLowerCase().includes(query) ||
            item.a.toLowerCase().includes(query),
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [search, activeCategory]);

  const handleFeedback = (helpful: boolean) => {
    toast(helpful ? "Thanks for the feedback!" : "Thanks — we'll keep improving.", {
      description: helpful ? "Glad we could help." : "Reach out if you still need a hand.",
    });
  };

  return (
    <div>
      {/* Hero */}
      <div className="relative w-full h-[50vh] min-h-[320px] overflow-hidden">
        <img src={heroFaqs} alt="Coyote Creek hats and lifestyle gear arranged on a rustic wooden surface" className="absolute inset-0 h-full w-full object-cover" width={1920} height={768} />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
          <h1 className="font-heading text-5xl md:text-7xl uppercase tracking-[0.15em] text-white drop-shadow-lg">FAQs</h1>
        </div>
      </div>

      {/* Search + Category chips */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-4xl px-6 py-10">
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search questions…"
              aria-label="Search questions"
              className="h-12 rounded-full border-border pl-12 pr-4 text-base shadow-sm"
            />
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
            {ALL_CATEGORIES.map((cat) => {
              const isActive = cat === activeCategory;
              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`rounded-full border px-4 py-2 font-ui text-sm transition-colors ${
                    isActive
                      ? "border-blaze bg-blaze text-[#F5EFE2]"
                      : "border-border bg-card text-primary hover:border-blaze hover:text-blaze"
                  }`}
                  aria-pressed={isActive}
                >
                  {cat}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Grouped accordions */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-6">
          {filteredGroups.length === 0 ? (
            <div className="rounded-xl border border-border bg-card p-10 text-center">
              <p className="font-heading text-xl text-primary">No matches found</p>
              <p className="mt-2 text-muted-foreground">
                Try a different search term or clear your filter.
              </p>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredGroups.map((group) => (
                <div key={group.category}>
                  <h2 className="font-heading text-2xl md:text-3xl uppercase tracking-wide text-primary">
                    {group.category}
                  </h2>
                  <div className="mt-4 rounded-xl border border-border bg-card">
                    <Accordion type="single" collapsible className="w-full">
                      {group.items.map((item, idx) => (
                        <AccordionItem
                          key={item.q}
                          value={`${group.category}-${idx}`}
                          className="border-b border-border last:border-b-0 px-6"
                        >
                          <AccordionTrigger className="py-5 text-left font-heading text-lg uppercase tracking-wide text-primary hover:no-underline">
                            {item.q}
                          </AccordionTrigger>
                          <AccordionContent className="pb-6 text-muted-foreground">
                            <p className="text-base leading-relaxed">{item.a}</p>
                            <div className="mt-5 flex items-center gap-3 text-sm">
                              <span className="text-muted-foreground">Was this helpful?</span>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFeedback(true)}
                                aria-label="Helpful"
                                className="h-8 gap-1 px-2 text-muted-foreground hover:text-primary"
                              >
                                <ThumbsUp className="h-4 w-4" />
                                Yes
                              </Button>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleFeedback(false)}
                                aria-label="Not helpful"
                                className="h-8 gap-1 px-2 text-muted-foreground hover:text-primary"
                              >
                                <ThumbsDown className="h-4 w-4" />
                                No
                              </Button>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still need help */}
      <section className="pb-24">
        <div className="mx-auto max-w-5xl px-6">
          <div className="grid grid-cols-1 items-center gap-10 border-t border-primary/15 pt-16 md:grid-cols-12 md:gap-16">
            <div className="md:col-span-7">
              <span className="font-ui text-xs font-bold uppercase tracking-[0.3em] text-primary/60">
                Still Have Questions?
              </span>
              <h2 className="mt-4 font-heading text-3xl font-black uppercase tracking-tight text-primary md:text-5xl">
                We're happy to help.
              </h2>
              <div className="mt-6 h-1 w-12" style={{ backgroundColor: "#FA5021" }} />
              <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-primary/70 md:text-lg">
                Can't find what you're looking for? Drop us a line, or start a custom order — a real person will get back to you.
              </p>
            </div>
            <div className="flex flex-col gap-4 md:col-span-5 md:items-end">
              <Link to="/contact" className="btn-cc-accent tracking-[0.2em]">
                Contact Us
              </Link>
              <a
                href="https://gwestcustoms.coyotecreekhatco.com"
                className="btn-cc-primary tracking-[0.2em]"
              >
                Custom Orders
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
